import { lowerNoun } from "../lib/locale";
import {
  ageBracketLabels,
  guestCounterMins,
  isDayOpen,
  isDayTooSoon,
  filterNoticeTimes,
  lastRestaurantBookingDate,
  normalizeMinPeople,
  timeSlotLabel,
  sameDayCutoffPassed,
  WHATSAPP_NUMBER_E164,
  type TablePolicy,
} from "@majourneys/shared";
import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { latnLocale } from "../lib/format";
import {
  DEFAULT_RESTAURANT_TIME_SLOTS,
  restaurantSeatingsOn,
  buildPackageWindowSlots,
  filterFutureTimes,
  groupDepositFor,
  moroccoStartOfToday,
  paysByLink,
  sortServiceTimes,
  takesMultiplePacks,
} from "@majourneys/shared";
import { guestCapacity } from "../lib/calc-total";
import { useBookingUnitLabels } from "../lib/booking-unit";
import { groupsForParty, initialSidebarParty, sellsAdultsOnly, sidebarShownTotal } from "../lib/sidebar-party";
import { useDisplayMoney, useDisplayPriceParts } from "../lib/currency";
import { NeedHelpCard } from "../internal-ui/need-help-card";
import { useAvailability } from "../hooks/use-availability";
import { ageSubLabels, tableChildrenSubLabel } from "../lib/age-labels";
import { toLocalISO, parseLocalISO } from "../lib/local-date";
import { CalendarGrid, diffDaysISO } from "./booking-calendar";
import { StayRangePicker } from "./stay-range-picker";
import {
  fewestSeatsLeft,
  hasFixedTripDates,
  isTripFull,
  openTripDates,
  tripDateList,
  tripDepartureList,
  tripMoneyRows,
  tripSeatsLeftOn,
  waitingListHref,
  type WomenTripInfo,
} from "../lib/women-trip";
import { TripDeparturePicker, TripMoneyRowsView, TripNote, TripSeatsBar, useDepositNote } from "./women-trip-parts";

import { Icon } from "@majourneys/icons";
import { useBookingSkin, type BookingSkin } from "../skin";
import { PW, pewterFooter, pewterInset, pewterPanel } from "../internal-ui/pewter-shell";
import { SaveToastButton } from "../internal-ui/save-toast";
import { BEVEL_CHIP_ON, BEVEL_INSET_NAVY, BEVEL_NAVY_FACE } from "@majourneys/shared/ui";
const PRICE_LABEL_KEYS: Record<number, string> = {
  0: "activity.person",
  1: "activity.hour",
  2: "activity.night",
  3: "activity.group",
};

type GuestType = "adults" | "children" | "infants";
type Guests = Record<GuestType, number>;

const GUEST_META: Record<
  GuestType,
  { labelKey: string; subKey: string; defaultLabel: string; defaultSub: string; icon: string; min: number }
> = {
  adults: {
    labelKey: "activity.adults",
    defaultLabel: "Adults",
    subKey: "activity.adults_age",
    defaultSub: "16+ years",
    icon: "ri-user-3-line",
    min: 1,
  },
  children: {
    labelKey: "activity.children",
    defaultLabel: "Children",
    subKey: "activity.children_age",
    defaultSub: "3-15 years",
    icon: "ri-user-smile-line",
    min: 0,
  },
  infants: {
    labelKey: "activity.infants",
    defaultLabel: "Infants",
    subKey: "activity.infants_age",
    defaultSub: "0-2 years",
    icon: "ri-bear-smile-line",
    min: 0,
  },
};

/** What the guest picked in the sidebar, handed to the booking sheet on open. */
export interface BookingSelection {
  date?: string;
  checkOut?: string;
  time?: string;
  adults?: number;
  children?: number;
  infants?: number;
  groups?: number;
  /** Stays: tents or lodges booked. */
  rooms?: number;
  /** Spas: who is coming (set by a MaJourneys AI proposal link). */
  women?: number;
  men?: number;
  /** A women-only trip: the city she leaves from. */
  tripDeparture?: string;
}

export interface ActivitySidebarProps {
  slug: string;
  cityName: string;
  /** Drives the per-group unit noun — "quad" prices per quad, not per party. */
  categorySlug?: string | null;
  price: number;
  typePrice: 0 | 1 | 2 | 3;
  thumbnail?: string;
  title: string;
  packagesCount: number;
  /** Restaurant table reservation: one party size, no package, no price shown. */
  isTableFlow?: boolean;
  /** The venue books through its own sheet at every width, never the /packages
   *  pages: a nightclub's three-step flow, like a restaurant's table flow. */
  sheetOnly?: boolean;
  packages?: Array<{
    timeStart?: string;
    timeEnd?: string;
    timeSlots?: readonly string[] | null;
    /** The window is one departure, its hour confirmed later: "13:00–15:00". */
    flexibleTime?: boolean | null;
    maxGuests?: number | null;
    includedGuests?: number | null;
    pricing?: {
      adult: number;
      child: number;
      infant: number;
      transport: number | null;
      extraGuest?: number | null;
    };
  }>;
  /** Fixed daily session times ("HH:mm") set on the activity. They outrank the
   * packages' opening windows: an appointment-based venue means those exact
   * starts, and a half-hour start like 15:30 has no hourly-grid equivalent. */
  fixedTimeSlots?: readonly string[] | null;
  /** Restaurant house rules; its `sameDayCutoff` closes tonight at a set hour. */
  tablePolicy?: TablePolicy | null;
  /** Weekdays the venue takes bookings on; null means every day. */
  openDays?: readonly string[] | null;
  /** Hours of notice the provider needs; null when none. */
  minNoticeHours?: number | null;
  /** `activities.opening_hours`, raw. A restaurant's seatings follow each
   * day's hours, until an hour before closing (`restaurantSeatingsOn`). */
  openingHours?: unknown;
  lang: string;
  /** Accepted, deliberately not rendered (owner, 2026-09-10). */
  freeCancellation?: boolean;
  /** Per-activity infant age ceiling (e.g. 2 → infants "Under 3"). When set
   * together with childrenMaxAge, drives dynamic guest age labels. */
  infantMaxAge?: number | null;
  /** Per-activity child age ceiling (e.g. 12 → children "3–12 years"). */
  childrenMaxAge?: number | null;
  /** Minimum stay length for typePrice=2 (nights). Defaults to 1. */
  minNights?: number;
  /** Smallest party the activity takes (adults + children). Defaults to 1. */
  minPeople?: number;
  /** When provided, the responsive (below `lg`) Book-now CTA opens the in-page
   * booking sheet via this callback instead of navigating. Desktop CTA is
   * unchanged. */
  /**
   * Opens the booking sheet. Receives what the guest already chose here, so the
   * sheet continues their selection instead of starting from its own defaults
   * (or, worse, restoring a stale saved draft with a different date).
   */
  onBookSheet?: (selection?: BookingSelection) => void;
  /** DI: current wishlisted state. When undefined the save button is hidden. */
  wishlisted?: boolean;
  /** DI: called when user toggles the wishlist button. When undefined the save
   * button is hidden. */
  onToggleWishlist?: () => void;
  /** DI: render the share modal. Receives a close callback; return null when
   * not open. The sidebar controls open state internally. */
  renderShareModal?: (props: { url: string; title: string; thumbnail?: string; cityName: string; price: number; onClose: () => void }) => React.ReactNode;
  /**
   * A women-only group trip: fixed dates with seats left, a deposit paid to
   * the organiser. Null (or omitted) for every other activity.
   */
  womenTrip?: WomenTripInfo | null;
  /**
   * What the address already says (`?date=&adults=&children=`), so the panel
   * starts on the guest's own day and party instead of its defaults and
   * quotes the same total as the booking sheet beside it.
   */
  initialSelection?: BookingSelection | null;
}

function buildDays(count: number, lang: string) {
  // Morocco's day, not the host's: see moroccoStartOfToday.
  const base = moroccoStartOfToday();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    return {
      iso: toLocalISO(d),
      day: d.toLocaleDateString(lang, { weekday: "short" }),
      num: d.getDate(),
      month: d.toLocaleDateString(lang, { month: "short" }),
    };
  });
}

/** One quick-strip tile for a given day (a trip's fixed date). */
function dayTile(iso: string, lang: string) {
  const d = parseLocalISO(iso);
  return {
    iso,
    day: d.toLocaleDateString(lang, { weekday: "short" }),
    num: d.getDate(),
    month: d.toLocaleDateString(lang, { month: "short" }),
  };
}

function formatDate(iso: string, lang: string) {
  return parseLocalISO(iso).toLocaleDateString(lang, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}


interface AvailabilitySlot {
  time: string;
  spotsLeft: number;
}

function parseTimeToMinutes(value?: string) {
  if (!value) return null;
  const match = value.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

// Default daytime window used when the operator has not set explicit
// timeStart / timeEnd on a package — 09:00 to 18:00, mirroring the booking
// sheet so both steps of the funnel offer the same hours.

/**
 * Sidebar surface treatment ("Soft stack", prototyped on
 * /booking-sidebar-versions-lab). No borders: a four-stop diffuse shadow with
 * a white inset highlight separates the card from the page, inset grey marks
 * anything editable, and the surfaces are gradients so the card reads as lit
 * from the top-left.
 *
 * Kept as inline constants rather than a utility class because this package is
 * also consumed by the city sites, which do not load the web app's stylesheet.
 */
const SIDEBAR_SOFT_SHADOW =
  "0 1.67px 4.18px rgba(17,14,57,0.10), 0 17.57px 10.88px rgba(17,14,57,0.06), 0 31.8px 12.55px rgba(17,14,57,0.02), 0 50.21px 14.23px rgba(17,14,57,0.01), inset 0 7px 32px #FFFFFF";
const SIDEBAR_CARD_GRADIENT =
  "linear-gradient(158deg,#FFFFFF 0%,#FCFBFE 46%,#F6F3FA 100%)";
const SIDEBAR_FOOTER_GRADIENT =
  "linear-gradient(158deg,#F5F3F9 0%,#F2F0F7 55%,#F6F1EA 100%)";
const SIDEBAR_CTA_GRADIENT = BEVEL_NAVY_FACE;
const SIDEBAR_INSET = "#F2F1F6";

/**
 * The same surfaces in the two skins the sidebar can wear. Classic is the soft
 * stack above; pewter is the framed card the rest of the new design uses (see
 * `internal-ui/pewter-shell.ts`). Everything below reads one of these rather
 * than the constants, so a skin is a lookup and not a second sidebar.
 */
interface SidebarSkin {
  card: React.CSSProperties;
  popover: React.CSSProperties;
  pill: React.CSSProperties;
  footer: React.CSSProperties;
  inset: React.CSSProperties;
  cta: string;
  insetColor: string;
}

const SIDEBAR_SKINS: Record<BookingSkin, SidebarSkin> = {
  classic: {
    card: { background: SIDEBAR_CARD_GRADIENT, boxShadow: SIDEBAR_SOFT_SHADOW },
    popover: { background: SIDEBAR_CARD_GRADIENT, boxShadow: SIDEBAR_SOFT_SHADOW },
    pill: { background: "#FFFFFF", boxShadow: SIDEBAR_SOFT_SHADOW },
    footer: { background: SIDEBAR_FOOTER_GRADIENT },
    inset: { background: SIDEBAR_INSET },
    cta: SIDEBAR_CTA_GRADIENT,
    insetColor: SIDEBAR_INSET,
  },
  pewter: {
    card: pewterPanel(24, { fill: PW.sheetFill }),
    popover: pewterPanel(16),
    pill: { background: "rgba(255,255,255,0.9)", boxShadow: `0 0 0 1px ${PW.innerHairline}` },
    footer: pewterFooter,
    inset: pewterInset,
    cta: PW.navyGradient,
    insetColor: PW.softTint,
  },
};

/**
 * Union of the opening windows the packages declare. When no package declares
 * one, this returns none on purpose so the caller falls back to the
 * availability API — the only place an activity's fixed session times
 * (`time_slots`, e.g. 15:30) are known. A default grid here would shadow them.
 */
/**
 * Exact departures declared on the package, normalised to "HH:mm". A window
 * can only be walked in one-hour steps, so a package that leaves at 14:00 and
 * 16:00 and nothing between needs its own list.
 */
function packageFixedSlots(pkg: { timeSlots?: readonly string[] | null }): string[] {
  return (pkg.timeSlots ?? [])
    .filter((slot): slot is string => typeof slot === "string" && slot.length > 0)
    .map((slot) => slot.slice(0, 5));
}

export function buildTimeSlotsFromPackages(
  packages?: Array<{ timeStart?: string; timeEnd?: string; timeSlots?: readonly string[] | null }>,
) {
  const list = packages ?? [];
  const anyDeclared = list.some(
    (pkg) =>
      packageFixedSlots(pkg).length > 0 ||
      parseTimeToMinutes(pkg.timeStart) != null ||
      parseTimeToMinutes(pkg.timeEnd) != null,
  );
  if (!anyDeclared) return [];
  const slots = new Set<string>();
  for (const pkg of list) {
    // A package with exact departures contributes only those.
    const fixed = packageFixedSlots(pkg);
    if (fixed.length > 0) {
      for (const slot of fixed) slots.add(slot);
      continue;
    }
    // Once any sibling declares a window, a package without one is bookable
    // across the default day window — skipping it would let one morning-only
    // package hide every afternoon slot of its all-day siblings.
    //
    // `buildPackageWindowSlots` owns the midnight rule: "closes at 00:00"
    // means midnight, not minute zero of the same morning. Marajah Spa's
    // packages are all saved 09:00–00:00 and this surface used to drop every
    // one of them, leaving the guest with whatever the availability API
    // happened to return.
    for (const slot of buildPackageWindowSlots(pkg, "full")) slots.add(slot);
  }
  // Service order, not alphabetical: a restaurant serving 19:00–01:00 must end
  // on its small hours, not open with them. See `sortServiceTimes`.
  return sortServiceTimes([...slots]);
}

function getGuestPrices(
  packages: ActivitySidebarProps["packages"],
  fallbackAdultPrice: number,
) {
  const pricing = packages?.[0]?.pricing;
  return {
    adults: pricing?.adult ?? fallbackAdultPrice,
    children: pricing?.child ?? 0,
    infants: pricing?.infant ?? 0,
  };
}

function TimeSlotsList({
  slots,
  selectedTime,
  onSelectTime,
  labelFor = (slot) => slot,
}: {
  readonly slots: string[];
  readonly selectedTime: string | null;
  readonly onSelectTime: (time: string) => void;
  /** What a chip reads: a flexible package's window shows as "13:00–15:00". */
  readonly labelFor?: (slot: string) => string;
}) {
  const skin = useBookingSkin();
  const S = SIDEBAR_SKINS[skin];
  if (slots.length === 0)
    return <p className="font-sans text-[12px] text-[#5a5d77]">—</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {slots.map((time) => {
        const active = selectedTime === time;
        return (
          <button
            key={time}
            type="button"
            onClick={() => onSelectTime(time)}
            aria-pressed={active}
            className={`flex min-h-9 min-w-[68px] items-center justify-center px-3 py-1.5 font-sans text-[11.5px] font-bold transition ${
              skin === "pewter" ? "mj-pw-chip" : "rounded-2xl"
            } ${active ? "text-white" : "text-[#110E39]"}`}
            style={skin === "pewter" ? undefined : { background: active ? S.cta : S.insetColor, boxShadow: active ? BEVEL_CHIP_ON : undefined }}
          >
            <span dir="ltr">{labelFor(time)}</span>
          </button>
        );
      })}
    </div>
  );
}

function ApiAvailabilityList({
  slug,
  date,
  selectedTime,
  onSelectTime,
  labelFor,
}: {
  readonly slug: string;
  readonly date: string;
  readonly selectedTime: string | null;
  readonly onSelectTime: (time: string) => void;
  readonly labelFor?: (slot: string) => string;
}) {
  const { data: response } = useAvailability(slug, date);
  const slots = ((response.data as AvailabilitySlot[] | undefined) ?? [])
    .filter((slot) => slot.spotsLeft > 0)
    .map((slot) => slot.time);
  return (
    <TimeSlotsList slots={slots} selectedTime={selectedTime} onSelectTime={onSelectTime} labelFor={labelFor} />
  );
}

/**
 * Time picker for the detail sidebar. Prefers the package's fixed window
 * (`rawSlots`); when none is configured it falls back to the live availability
 * API. Past times for the chosen date are already removed from `visibleSlots`;
 * if that empties a configured window, the day reads as fully past.
 */
function TimeSlotsSection({
  rawSlots,
  visibleSlots,
  slug,
  date,
  selectedTime,
  onSelectTime,
  labelFor,
  sameDayCutoff = null,
  title = "",
}: {
  readonly rawSlots: string[];
  readonly visibleSlots: string[];
  /** Cutoff time when same-night booking has closed for `date`, else null. */
  readonly sameDayCutoff?: string | null;
  readonly title?: string;
  readonly slug: string;
  readonly date: string;
  readonly selectedTime: string | null;
  readonly onSelectTime: (time: string) => void;
  readonly labelFor?: (slot: string) => string;
}) {
  const { t } = useTranslation();
  if (sameDayCutoff) {
    const waText = t("activity.same_day_whatsapp_message", {
      defaultValue: "Hi! I'd like a table at {{title}} tonight.",
      title,
    });
    return (
      <div>
        <p className="font-sans text-[12px] font-semibold text-[#7b5a2a]">
          {t("activity.same_day_closed", {
            defaultValue:
              "Online booking for tonight closed at {{time}}. Pick another day, or message us on WhatsApp.",
            time: sameDayCutoff,
          })}
        </p>
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER_E164.replace(/^\+/, "")}?text=${encodeURIComponent(waText)}`}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex min-h-[32px] items-center gap-1.5 font-sans text-[12.5px] font-bold text-[#128C4A] underline-offset-2 hover:underline"
        >
          <Icon name="ri-whatsapp-line" className="text-[16px]" aria-hidden />
          {t("activity.message_us_whatsapp", { defaultValue: "Message us on WhatsApp" })}
        </a>
      </div>
    );
  }
  if (rawSlots.length === 0) {
    return (
      <Suspense fallback={<p className="font-sans text-[12px] text-[#5a5d77]">…</p>}>
        <ApiAvailabilityList
          slug={slug}
          date={date}
          selectedTime={selectedTime}
          onSelectTime={onSelectTime}
          labelFor={labelFor}
        />
      </Suspense>
    );
  }
  if (visibleSlots.length === 0) {
    return (
      <p className="font-sans text-[12px] font-semibold text-[#7b5a2a]">
        {t("activity.no_times_today", {
          defaultValue:
            "No more times available for this date. Please pick another day.",
        })}
      </p>
    );
  }
  return (
    <TimeSlotsList
      slots={visibleSlots}
      selectedTime={selectedTime}
      onSelectTime={onSelectTime}
      labelFor={labelFor}
    />
  );
}


function Stepper({
  value,
  onChange,
  min = 0,
  max = 20,
}: {
  readonly value: number;
  readonly onChange: (v: number) => void;
  readonly min?: number;
  readonly max?: number;
}) {
  const { t, i18n } = useTranslation();
  const numberFmt = useMemo(() => new Intl.NumberFormat(latnLocale(i18n.language)), [i18n.language]);
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        aria-label={t("common.decrease", { defaultValue: "Decrease" })}
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-[rgba(4,0,51,0.12)] bg-white text-[14px] font-bold text-[#110E39] transition hover:bg-[rgba(4,0,51,0.05)] disabled:cursor-not-allowed disabled:opacity-35"
      >
        −
      </button>
      <span className="min-w-[18px] text-center font-sans text-[13.5px] font-bold text-[#110E39]">
        {numberFmt.format(value)}
      </span>
      <button
        type="button"
        aria-label={t("common.increase", { defaultValue: "Increase" })}
        disabled={value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-[rgba(4,0,51,0.12)] bg-white text-[14px] font-bold text-[#110E39] transition hover:bg-[rgba(4,0,51,0.05)] disabled:cursor-not-allowed disabled:opacity-35"
      >
        +
      </button>
    </div>
  );
}

export function ActivitySidebar({
  slug,
  cityName,
  categorySlug,
  price,
  typePrice,
  thumbnail,
  title,
  packagesCount,
  isTableFlow = false,
  sheetOnly = false,
  packages,
  fixedTimeSlots,
  tablePolicy = null,
  openDays = null,
  minNoticeHours = null,
  openingHours,
  lang,
  infantMaxAge,
  childrenMaxAge,
  minNights = 1,
  minPeople = 1,
  onBookSheet,
  wishlisted = false,
  onToggleWishlist,
  renderShareModal,
  womenTrip = null,
  initialSelection = null,
}: ActivitySidebarProps) {
  const { t, i18n } = useTranslation();
  const skin = useBookingSkin();
  const S = SIDEBAR_SKINS[skin];
  const unitLabels = useBookingUnitLabels(categorySlug, slug);
  const fmt = (n: number) => new Intl.NumberFormat(latnLocale(i18n.language)).format(n);
  const ageLabels = useMemo(
    () => ageSubLabels(t, { infantMaxAge, childrenMaxAge }),
    [t, infantMaxAge, childrenMaxAge],
  );
  const tableChildrenSub = tableChildrenSubLabel(t, { infantMaxAge, childrenMaxAge });
  const formatPriceParts = useDisplayPriceParts();
  const mad = formatPriceParts(0).currency;
  const fmtMoney = (n: number) => formatPriceParts(n).value;
  const money = useDisplayMoney();

  // Morocco's today, for the trip's upcoming dates.
  const todayIso = useMemo(() => toLocalISO(moroccoStartOfToday()), []);
  const fixedTripDates = hasFixedTripDates(womenTrip);
  const tripDates = useMemo(() => tripDateList(womenTrip, todayIso), [womenTrip, todayIso]);
  const tripOpenDates = useMemo(() => openTripDates(womenTrip, todayIso), [womenTrip, todayIso]);
  const tripFull = isTripFull(womenTrip, todayIso);
  // Where she leaves from, carried into the sheet. One city is picked for her.
  const tripDepartures = useMemo(() => tripDepartureList(womenTrip), [womenTrip]);
  const [tripDeparture, setTripDeparture] = useState<string | null>(() =>
    tripDepartures.length === 1 ? tripDepartures[0]! : null,
  );
  // A trip on one date has nothing to choose: it opens on it.
  const [date, setDate] = useState<string | null>(() => {
    if (fixedTripDates && tripOpenDates.length === 1) return tripOpenDates[0]!;
    // The day from the address, when the venue can still take it.
    const wanted = initialSelection?.date;
    if (
      wanted &&
      !fixedTripDates &&
      wanted >= todayIso &&
      isDayOpen(wanted, openDays) &&
      !isDayTooSoon(wanted, minNoticeHours)
    ) {
      return wanted;
    }
    return null;
  });
  // A stay's check-out from the address, kept only with the check-in it follows.
  const [checkOut, setCheckOut] = useState<string | null>(() =>
    typePrice === 2 && date && initialSelection?.date === date && initialSelection.checkOut
      ? initialSelection.checkOut
      : null,
  );
  const [selectedTime, setSelectedTime] = useState<string | null>(() => {
    // The hour from the address, only on a day it kept and a time this
    // activity runs at.
    const wanted = initialSelection?.time;
    if (!wanted || !initialSelection?.date) return null;
    const known = [...(fixedTimeSlots ?? []), ...buildTimeSlotsFromPackages(packages)];
    return known.includes(wanted) ? wanted : null;
  });
  const [expandedCalendar, setExpandedCalendar] = useState(false);
  /* Only one picker is open at a time. Left open, the calendar (231px) and
     the full slot list (~200px) together pushed the card past the viewport
     and shoved the Book CTA below the fold. */
  const [timesOpen, setTimesOpen] = useState(false);
  // Default to 1 adult — visitors land on the page solo more often than as a
  // couple, and a 1-adult preview won't surprise solo travelers with an
  // inflated total before they touch the picker.
  // A trip with a minimum party (a private day trip priced for two) starts
  // there instead, so the preview total is one the checkout will accept.
  const minParty = normalizeMinPeople(minPeople);
  // The party from the address when there is one, as the sheet does.
  const [initialParty] = useState(() =>
    initialSidebarParty({
      selection: initialSelection,
      minParty,
      typePrice,
      // A restaurant seats children whatever its package rows say.
      pricing: isTableFlow ? null : packages?.[0]?.pricing ?? null,
      unitCap: packages?.[0]?.maxGuests ?? null,
    }),
  );
  const [guests, setGuests] = useState<Guests>({
    adults: initialParty.adults,
    children: initialParty.children,
    infants: initialParty.infants,
  });
  const [draftGuests, setDraftGuests] = useState<Guests>(guests);
  // Desktop layout state — controls the standalone stay-picker popover and guests editor.
  const [stayPicker, setStayPicker] = useState<"in" | "out" | null>(null);
  const [guestsOpen, setGuestsOpen] = useState(false);
  // Mobile/tablet layout state — controls the When/Who pill-bar drawer.
  const [openPill, setOpenPill] = useState<"when" | "who" | null>(null);
  const [groups, setGroups] = useState(initialParty.groups);
  const [shareOpen, setShareOpen] = useState(false);

  const stayPickerRef = useRef<HTMLDivElement>(null);
  const guestWrapRef = useRef<HTMLDivElement>(null);
  const pillBarRef = useRef<HTMLDivElement>(null);

  // Per-group activities (typePrice 3) declare `maxGuests` per unit — one quad
  // seats 2 — so the headcount ceiling grows with the number of units booked.
  const maxGuests = useMemo(() => {
    const caps = (packages ?? []).map((p) => p.maxGuests).filter((v): v is number => typeof v === "number" && v > 0);
    const packageCap = caps.length === 0 ? null : guestCapacity(typePrice, Math.max(...caps), groups) || null;
    // A trip seats no more than the seats left on the chosen date.
    const seats = tripSeatsLeftOn(womenTrip, date);
    if (seats != null && seats > 0) return packageCap == null ? seats : Math.min(packageCap, seats);
    return packageCap;
  }, [packages, typePrice, groups, womenTrip, date]);
  /*
   * A camp's tent or lodge sleeps its `maxGuests`; a bigger party books more
   * than one (website test, 2026-11-01). On a stay `groups` counts them.
   */
  const stayUnitsOn =
    typePrice === 2 && (packages ?? []).some((p) => typeof p.maxGuests === "number" && p.maxGuests > 0);

  const nights = useMemo(() => {
    if (typePrice !== 2) return 1;
    if (date && checkOut) return Math.max(minNights, diffDaysISO(date, checkOut));
    return minNights;
  }, [typePrice, date, checkOut, minNights]);

  // Skip closed weekdays so the strip still offers seven bookable days.
  // A trip on fixed dates offers those dates, and only those with a seat.
  const days = useMemo(
    () =>
      fixedTripDates
        ? tripOpenDates.slice(0, 7).map((iso) => dayTile(iso, i18n.language))
        : buildDays(35, i18n.language)
            .filter((d) => isDayOpen(d.iso, openDays) && !isDayTooSoon(d.iso, minNoticeHours))
            .slice(0, 7),
    [i18n.language, openDays, minNoticeHours, fixedTripDates, tripOpenDates],
  );
  /** The full calendar's veto: closed weekdays, notice, and a full trip date. */
  const calendarDisabled = (iso: string) =>
    !isDayOpen(iso, openDays) || isDayTooSoon(iso, minNoticeHours) || tripSeatsLeftOn(womenTrip, iso) === 0;
  const calendarMaxDate = isTableFlow ? lastRestaurantBookingDate() : undefined;
  // Fixed activity sessions win over the packages' windows; without either, the
  // times come from the availability API.
  const rawPackageTimeSlots = useMemo(() => {
    const fixed = (fixedTimeSlots ?? []).filter((s) => typeof s === "string" && s.length > 0);
    // A restaurant with opening hours seats from opening until an hour before
    // that day's close (owner, 2026-10-07), its own times kept inside that.
    if (isTableFlow && date) {
      const seatings = restaurantSeatingsOn(
        openingHours,
        date,
        fixed,
        tablePolicy?.lastSeatingBeforeCloseMin,
      );
      if (seatings !== null) return seatings;
    }
    if (fixed.length > 0) return [...fixed];
    // A restaurant's seatings are its own, never a package's opening window —
    // and the availability API's 09:00–19:00 default is an activity's day, not
    // a service. Until the venue's `time_slots` are entered, offer lunch and
    // dinner rather than a morning grid nobody can be seated in.
    if (isTableFlow) return [...DEFAULT_RESTAURANT_TIME_SLOTS];
    return buildTimeSlotsFromPackages(packages);
  }, [fixedTimeSlots, packages, isTableFlow, openingHours, tablePolicy, date]);
  /** A slot that opens a flexible package's window reads "13:00–15:00". */
  const slotLabel = (slot: string): string => {
    for (const p of packages ?? []) {
      const label = timeSlotLabel(slot, p);
      if (label !== slot.slice(0, 5)) return label;
    }
    return slot;
  };
  // Drop times already passed for the chosen date so a date of "today" never
  // offers (or pre-selects) an hour that is already gone.
  // A restaurant may close same-night booking at a set hour (L'Octave: Friday
  // and Saturday from 17:00); tonight then offers no times at all.
  const sameDayCutoff = isTableFlow ? sameDayCutoffPassed(tablePolicy, date) : null;
  const packageTimeSlots = useMemo(
    () =>
      sameDayCutoff
        ? []
        : filterNoticeTimes(filterFutureTimes(rawPackageTimeSlots, date ?? ""), date ?? "", minNoticeHours),
    [sameDayCutoff, rawPackageTimeSlots, date, minNoticeHours],
  );
  const guestPrices = useMemo(() => getGuestPrices(packages, price), [packages, price]);
  const availableGuestTypes = useMemo(
    () =>
      // A restaurant takes adults and children as two counts that add up to
      // the table, never one number with the children hidden inside it
      // (owner, 2026-10-23). No infant row: babies count as children.
      isTableFlow
        ? (["adults", "children"] as GuestType[])
        : (Object.keys(GUEST_META) as GuestType[]).filter((type) => guestPrices[type] >= 0),
    [guestPrices, isTableFlow],
  );

  useEffect(() => {
    if (!openPill) return;
    function handle(e: MouseEvent) {
      if (pillBarRef.current && !pillBarRef.current.contains(e.target as Node))
        setOpenPill(null);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [openPill]);

  useEffect(() => {
    if (!stayPicker) return;
    function handle(e: MouseEvent) {
      if (stayPickerRef.current && !stayPickerRef.current.contains(e.target as Node))
        setStayPicker(null);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [stayPicker]);

  useEffect(() => {
    if (!guestsOpen) return;
    function handle(e: MouseEvent) {
      if (guestWrapRef.current && !guestWrapRef.current.contains(e.target as Node))
        setGuestsOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [guestsOpen]);

  const bookHrefBase = `/${lang}/${slug}/${typePrice === 2 ? "details" : "packages"}`;

  function selectDate(nextDate: string) {
    setDate(nextDate);
    setSelectedTime(null);
    setExpandedCalendar(false);
    setTimesOpen(true);
  }

  /* Picking an hour is the end of this step, so it collapses back to a summary
     row rather than leaving 16 slots on screen. */
  function selectTime(nextTime: string) {
    setSelectedTime(nextTime);
    setTimesOpen(false);
  }

  const bookSearch = useMemo(() => {
    const params = new URLSearchParams();
    if (date) params.set("date", date);
    if (selectedTime) params.set("time", selectedTime);
    if (guests.adults) params.set("adults", String(guests.adults));
    if (guests.children) params.set("children", String(guests.children));
    if (guests.infants) params.set("infants", String(guests.infants));
    if (typePrice === 2) {
      params.set("nights", String(nights));
      if (checkOut) {
        params.set("checkOut", checkOut);
        params.set("dateEnd", checkOut);
      }
    }
    if (typePrice === 3) params.set("groups", String(groups));
    if (stayUnitsOn && groups > 1) params.set("rooms", String(groups));
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, [date, selectedTime, guests, typePrice, nights, groups, checkOut, stayUnitsOn]);

  // Same selection as `bookSearch`, but for the sheet, which is opened by
  // callback rather than navigated to.
  const bookSelection = useMemo<BookingSelection>(
    () => ({
      date: date || undefined,
      checkOut: typePrice === 2 ? checkOut || undefined : undefined,
      time: selectedTime || undefined,
      adults: guests.adults || undefined,
      children: guests.children,
      infants: guests.infants,
      groups: typePrice === 3 ? groups : undefined,
      rooms: stayUnitsOn && groups > 1 ? groups : undefined,
      tripDeparture: tripDeparture ?? undefined,
    }),
    [date, checkOut, selectedTime, guests, typePrice, groups, tripDeparture, stayUnitsOn],
  );

  const url = typeof window !== "undefined" ? window.location.href : "";

  const totalGuests = guests.adults + guests.children + guests.infants;
  const draftTotal = draftGuests.adults + draftGuests.children + draftGuests.infants;

  const guestSummary = useMemo(() => {
    const parts: string[] = [];
    if (guests.adults > 0)
      parts.push(
        `${fmt(guests.adults)} ${guests.adults > 1 ? lowerNoun(t("activity.adults", { defaultValue: "adults" }), i18n.language) : lowerNoun(t("activity.adult", { defaultValue: "adult" }), i18n.language)}`,
      );
    if (guests.children > 0)
      parts.push(
        `${fmt(guests.children)} ${guests.children > 1 ? lowerNoun(t("activity.children", { defaultValue: "children" }), i18n.language) : lowerNoun(t("activity.child", { defaultValue: "child" }), i18n.language)}`,
      );
    if (guests.infants > 0)
      parts.push(
        `${fmt(guests.infants)} ${guests.infants > 1 ? lowerNoun(t("activity.infants", { defaultValue: "infants" }), i18n.language) : lowerNoun(t("activity.infant", { defaultValue: "infant" }), i18n.language)}`,
      );
    return parts.join(" · ") || t("activity.add_guests", { defaultValue: "Add guests" });
  }, [guests, t, fmt]);

  // A spa is booked treatment by treatment in the sheet, so before that the
  // panel has no real price per guest: the first package is just the cheapest
  // one. It says "from" and shows no per-guest price or total (owner,
  // 2026-10-10: "5 adults, 2,000 MAD" was 5 foot reflexologies nobody chose).
  const spaFlow = !isTableFlow && takesMultiplePacks(categorySlug ?? null);

  // Already in the guest's currency: each price rounded, then added up, the
  // way the booking sheet builds its total. Children count at their own price.
  const shownEstimate = useMemo(
    () =>
      sidebarShownTotal(
        {
          typePrice,
          price,
          pkg: packages?.[0] ?? null,
          guests,
          nights,
          // A stay counts lodges only when the guest can pick more than one.
          groups: typePrice === 2 && !stayUnitsOn ? 1 : groups,
        },
        money.unit,
      ),
    [typePrice, price, packages, guests, nights, groups, money, stayUnitsOn],
  );
  // Facts the panel can state from the packages alone, nothing invented.
  const adultsOnly = !isTableFlow && sellsAdultsOnly(packages);
  const adultMinAge = ageBracketLabels({ infantMaxAge, childrenMaxAge }).adults.params.min;
  const unitCap = typePrice === 3 ? packages?.[0]?.maxGuests ?? 0 : 0;

  const isRtl = i18n.language === "ar";
  const arrow = isRtl ? "←" : "→";
  const compactDate = (iso: string) =>
    parseLocalISO(iso).toLocaleDateString(i18n.language, { day: "numeric", month: "short" });

  const whenLabel = (() => {
    if (typePrice === 2) {
      if (!date && !checkOut) return t("activity.add_dates", { defaultValue: "Add dates" });
      if (date && !checkOut) return `${compactDate(date)} ${arrow}`;
      if (date && checkOut) return `${compactDate(date)} ${arrow} ${compactDate(checkOut)}`;
    }
    if (!date) return t("activity.add_date", { defaultValue: "Add date" });
    return selectedTime
      ? `${formatDate(date, i18n.language)} · ${selectedTime}`
      : formatDate(date, i18n.language);
  })();

  const whoLabel = (() => {
    if (typePrice === 3 && totalGuests === 0) {
      return `${fmt(groups)} ${unitLabels.count(groups)}`;
    }
    if (totalGuests === 0) return t("activity.add_guests", { defaultValue: "Add guests" });
    return guestSummary;
  })();

  return (
    <aside className="flex flex-col gap-3 lg:sticky lg:top-24">
      {/* Sections mount and unmount rather than animating their height: a
          transitioned `fr` track keeps a min-content minimum and never
          actually collapses. Enter-only animation, same timing and easing as
          the booking sheet's step transitions. */}
      <style>{`
        @keyframes mjStepIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: none; }
        }
        .mj-step-in { animation: mjStepIn 240ms cubic-bezier(0.16, 1, 0.3, 1) both; }
        @media (prefers-reduced-motion: reduce) {
          .mj-step-in { animation: none; }
        }
      `}</style>
      <div
        className="overflow-hidden rounded-3xl"
        style={S.card}
      >
        <div className="flex items-center justify-between px-5 py-3.5">
          <div className="flex min-w-0 items-center gap-2">
            <Icon name="ri-map-pin-line" className="text-[15px] text-[#E5B265]" aria-hidden="true" />
            <span className="min-w-0 break-words font-sans text-[13px] font-bold text-[#110E39]">
              {cityName}
            </span>
          </div>
          {/* No free-cancellation pill (owner, 2026-09-10). Only stays make
              that promise now, from LiteAPI's real per-rate policy. */}
        </div>

        <div className="p-5 lg:hidden">
          <div ref={pillBarRef}>
            <div
              className={
                skin === "pewter"
                  ? "grid grid-cols-2 overflow-hidden rounded-2xl"
                  : "grid grid-cols-2 overflow-hidden rounded-2xl border border-[rgba(4,0,51,0.1)] bg-white"
              }
              style={skin === "pewter" ? pewterPanel(16, { gap: 3, shadow: null }) : undefined}
            >
              <button
                type="button"
                onClick={() => setOpenPill((p) => (p === "when" ? null : "when"))}
                aria-expanded={openPill === "when"}
                className={`flex flex-col items-start gap-0.5 px-3.5 py-2.5 text-start transition ${
                  openPill === "when" ? "bg-[#f4f1ff]" : "hover:bg-[rgba(4,0,51,0.03)]"
                }`}
              >
                <span className="font-sans text-[10px] font-bold uppercase tracking-[0.1em] text-[#5a5d77]">
                  {typePrice === 2
                    ? t("activity.dates", { defaultValue: "Dates" })
                    : t("activity.when", { defaultValue: "When" })}
                </span>
                <span className="w-full break-words font-sans text-[13px] font-bold text-[#110E39]">
                  {whenLabel}
                </span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setDraftGuests(guests);
                  setOpenPill((p) => (p === "who" ? null : "who"));
                }}
                aria-expanded={openPill === "who"}
                className={`flex flex-col items-start gap-0.5 border-s border-[rgba(4,0,51,0.08)] px-3.5 py-2.5 text-start transition ${
                  openPill === "who" ? "bg-[#f4f1ff]" : "hover:bg-[rgba(4,0,51,0.03)]"
                }`}
              >
                <span className="font-sans text-[10px] font-bold uppercase tracking-[0.1em] text-[#5a5d77]">
                  {t("activity.who", { defaultValue: "Who" })}
                </span>
                <span className="w-full break-words font-sans text-[13px] font-bold text-[#110E39]">
                  {whoLabel}
                </span>
              </button>
            </div>

            {openPill === "when" && (
              <div className="mt-2 rounded-2xl p-3" style={S.popover}>
                {typePrice === 2 ? (
                  <>
                    <StayRangePicker
                      checkIn={date}
                      checkOut={checkOut}
                      minNights={minNights}
                      onChange={({ checkIn: a, checkOut: b }) => {
                        setDate(a);
                        setCheckOut(b);
                        setSelectedTime(null);
                        if (a && b) setOpenPill(null);
                      }}
                    />
                    {minNights > 1 && !checkOut && (
                      <p className="mt-2 font-sans text-[11px] text-[#5a5d77]">
                        <Icon name="ri-information-line" className="me-1" aria-hidden />
                        {t("activity.min_nights", { defaultValue: "Minimum stay" })}: {minNights}{" "}
                        {t("activity.nights_plural", { defaultValue: "nights" })}
                      </p>
                    )}
                    {/* No nights chip here: the picker's own header counts them. */}
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#5a5d77]">
                        {t("activity.pick_a_date", { defaultValue: "Pick a date" })}
                      </p>
                      <button
                        type="button"
                        onClick={() => setExpandedCalendar((v) => !v)}
                        aria-expanded={expandedCalendar}
                        className="flex min-h-[34px] cursor-pointer select-none items-center gap-1.5 rounded-full px-3.5 py-1 font-sans text-[11px] font-bold text-[#110E39] transition active:scale-[0.97] hover:-translate-y-[1px]"
                        style={S.pill}
                      >
                        <Icon name={expandedCalendar ? "ri-list-check" : "ri-calendar-line"} className={`text-[12px]`} aria-hidden="true" />
                        {expandedCalendar
                          ? t("activity.quick_view", { defaultValue: "Quick view" })
                          : t("activity.full_calendar", { defaultValue: "Full calendar" })}
                      </button>
                    </div>

                    {!expandedCalendar ? (
                      <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {days.map((d) => {
                          const active = date === d.iso;
                          return (
                            <button
                              key={d.iso}
                              type="button"
                              onClick={() => selectDate(d.iso)}
                              className={`flex min-w-[54px] flex-col items-center justify-center rounded-2xl px-2 py-2 font-sans transition ${
                                active ? "text-white" : "text-[#110E39]"
                              }`}
                              style={{
                                background: active ? S.cta : S.insetColor,
                                boxShadow: active ? BEVEL_CHIP_ON : undefined,
                              }}
                            >
                              <span
                                className={`text-[10px] font-bold uppercase tracking-[0.06em] ${active ? "text-white/70" : "text-[#5a5d77]"}`}
                              >
                                {d.day}
                              </span>
                              <span className="text-[18px] font-bold leading-none">{d.num}</span>
                              <span
                                className={`text-[10px] font-semibold ${active ? "text-white/70" : "text-[#5a5d77]"}`}
                              >
                                {d.month}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="mt-2">
                        <CalendarGrid selected={date} onSelect={selectDate} isDateDisabled={calendarDisabled} maxDate={calendarMaxDate} allowedDates={fixedTripDates ? tripDates : undefined} />
                      </div>
                    )}

                    {/* A trip with no departure time on its package has no time to pick. */}
                    {date && !(fixedTripDates && rawPackageTimeSlots.length === 0) && (
                      <div className="mt-3">
                        <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#5a5d77]">
                          {t("activity.available_times", { defaultValue: "Available times" })}
                        </p>
                        <div className="mt-2">
                          <TimeSlotsSection
                            rawSlots={rawPackageTimeSlots}
                            visibleSlots={packageTimeSlots}
                            slug={slug}
                            date={date}
                            selectedTime={selectedTime}
                            onSelectTime={selectTime}
                            labelFor={slotLabel}
                            sameDayCutoff={sameDayCutoff}
                            title={title}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {openPill === "who" && (
              <div className="mt-2 overflow-hidden rounded-2xl border border-[rgba(4,0,51,0.08)] bg-white/95 shadow-[0_24px_60px_rgba(4,0,51,0.18)]">
                <div className="flex items-center justify-between border-b border-[rgba(4,0,51,0.06)] bg-gradient-to-r from-[rgba(244,241,255,0.7)] to-white/60 px-4 py-3">
                  <div>
                    <p className="font-heading text-[15px] font-bold text-[#110E39]">
                      {t("activity.whos_coming", { defaultValue: "Who's coming?" })}
                    </p>
                    {maxGuests && (
                      <p className="mt-0.5 font-sans text-[11px] text-[#5a5d77]">
                        {t("activity.max_guests", { defaultValue: "Max" })} {maxGuests}{" "}
                        {lowerNoun(t("activity.guests", { defaultValue: "guests" }), i18n.language)}
                      </p>
                    )}
                  </div>
                  <span className="rounded-full bg-[rgba(29,22,89,0.06)] px-2 py-0.5 font-sans text-[11px] font-bold text-[#110E39]">
                    {draftTotal}{" "}
                    {draftTotal === 1
                      ? t("activity.guest", { defaultValue: "guest" })
                      : lowerNoun(t("activity.guests", { defaultValue: "guests" }), i18n.language)}
                  </span>
                </div>
                <div className="divide-y divide-[rgba(4,0,51,0.05)] px-4">
                  {typePrice === 3 && (
                    <div className="flex items-center gap-3 py-2">
                      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[rgba(29,22,89,0.06)]">
                        <Icon name="ri-team-line" className="text-[16px] text-[#110E39]" aria-hidden="true" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-sans text-[13.5px] font-bold leading-tight text-[#110E39]">
                          {unitLabels.title}
                        </p>
                        <p className="font-sans text-[11.5px] leading-snug text-[#5a5d77]">
                          {fmtMoney(price)} {mad} {unitLabels.per}
                        </p>
                      </div>
                      <Stepper value={groups} onChange={setGroups} min={1} max={20} />
                    </div>
                  )}
                  {stayUnitsOn && (
                    <div className="flex items-center gap-3 py-2" data-testid="sidebar-stay-units">
                      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[rgba(29,22,89,0.06)]">
                        <Icon name="ri-tent-line" className="text-[16px] text-[#110E39]" aria-hidden="true" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-sans text-[13.5px] font-bold leading-tight text-[#110E39]">
                          {t("booking_sheet.stay_units_title", { defaultValue: "Tents or lodges" })}
                        </p>
                        <p className="font-sans text-[11.5px] leading-snug text-[#5a5d77]">
                          {t("booking_sheet.stay_units_sub", {
                            defaultValue: "Each one priced per night",
                          })}
                        </p>
                      </div>
                      <Stepper value={groups} onChange={setGroups} min={1} max={10} />
                    </div>
                  )}
                  {availableGuestTypes.map((type) => {
                    const meta = GUEST_META[type];
                    const rowMin =
                      type === "infants" ? meta.min : guestCounterMins(minParty, draftGuests)[type];
                    return (
                      <div key={type} className="flex items-center gap-3 py-2">
                        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[rgba(29,22,89,0.06)]">
                          <Icon name={meta.icon} className={`text-[16px] text-[#110E39]`} aria-hidden="true" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="font-sans text-[13.5px] font-bold leading-tight text-[#110E39]">
                            {t(meta.labelKey, { defaultValue: meta.defaultLabel })}
                          </p>
                          <p className="font-sans text-[11.5px] leading-snug text-[#5a5d77]">
                            {isTableFlow ? (type === "children" ? tableChildrenSub : null) : ageLabels[type]}
                            {typePrice !== 2 && typePrice !== 3 && !isTableFlow && !spaFlow && (
                              <>
                                <span className="mx-1 text-[#c1c3d4]">·</span>
                                <span className="font-bold text-[#110E39]">
                                  {fmtMoney(guestPrices[type])} {mad}
                                </span>
                              </>
                            )}
                          </p>
                        </div>
                        <Stepper
                          value={draftGuests[type]}
                          onChange={(v) => setDraftGuests((g) => ({ ...g, [type]: v }))}
                          min={rowMin}
                          max={
                            maxGuests
                              ? Math.max(
                                  rowMin,
                                  draftGuests[type] +
                                    Math.max(
                                      0,
                                      maxGuests -
                                        (draftGuests.adults +
                                          draftGuests.children +
                                          draftGuests.infants),
                                    ),
                                )
                              : 20
                          }
                        />
                      </div>
                    );
                  })}
                </div>
                {minParty > 1 && (
                  <p className="px-4 pb-3 font-sans text-[11.5px] font-medium text-[#5a5d77]">
                    {t("activity.min_people", { count: minParty, defaultValue: "Minimum {{count}} people" })}
                  </p>
                )}
                <div className="flex items-center gap-2 border-t border-[rgba(4,0,51,0.06)] bg-[rgba(244,241,255,0.4)] p-3">
                  <button
                    type="button"
                    onClick={() => setDraftGuests({ adults: minParty, children: 0, infants: 0 })}
                    className="rounded-full px-3 py-1.5 font-sans text-[12px] font-bold text-[#5a5d77] transition hover:text-[#110E39]"
                  >
                    {t("common.reset", { defaultValue: "Reset" })}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setGuests(draftGuests);
                      setGroups((g) => groupsForParty({ typePrice, groups: g, guests: draftGuests, unitCap: packages?.[0]?.maxGuests ?? null }));
                      setOpenPill(null);
                    }}
                    className="ms-auto rounded-full bg-[linear-gradient(180deg,#2E2878_0%,#1A1454_50%,#110E39_100%)] px-4 py-2 font-sans text-[12.5px] font-bold text-white shadow-[0_6px_16px_rgba(17,14,57,0.28),inset_0_1.5px_0_rgba(255,255,255,0.22),inset_0_-1.5px_0_rgba(0,0,0,0.25)] transition hover:scale-[1.02]"
                  >
                    {t("common.apply", { defaultValue: "Apply" })}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Desktop stacked layout (lg+) — keeps the original date strip / time / guests rhythm */}
        <div className="hidden p-5 lg:block">
          {typePrice === 2 && (
            <div ref={stayPickerRef} className="relative mb-3">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setStayPicker((s) => (s === "in" ? null : "in"))}
                  className={`flex flex-col items-start gap-0.5 rounded-2xl border bg-white/70 px-3 py-2.5 text-start transition ${
                    stayPicker === "in"
                      ? "border-[#110E39]"
                      : "border-[rgba(4,0,51,0.08)] hover:border-[rgba(4,0,51,0.18)]"
                  }`}
                >
                  <span className="font-sans text-[10px] font-bold uppercase tracking-[0.08em] text-[#5a5d77]">
                    {t("activity.check_in", { defaultValue: "Check-in" })}
                  </span>
                  <span className="font-sans text-[14px] font-bold text-[#110E39]">
                    {date ? formatDate(date, i18n.language) : t("activity.add_date", { defaultValue: "Add date" })}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setStayPicker((s) => (s === "out" ? null : "out"))}
                  disabled={!date}
                  className={`flex flex-col items-start gap-0.5 rounded-2xl border bg-white/70 px-3 py-2.5 text-start transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    stayPicker === "out"
                      ? "border-[#110E39]"
                      : "border-[rgba(4,0,51,0.08)] hover:border-[rgba(4,0,51,0.18)]"
                  }`}
                >
                  <span className="font-sans text-[10px] font-bold uppercase tracking-[0.08em] text-[#5a5d77]">
                    {t("activity.check_out", { defaultValue: "Check-out" })}
                  </span>
                  <span className="font-sans text-[14px] font-bold text-[#110E39]">
                    {checkOut ? formatDate(checkOut, i18n.language) : t("activity.add_date", { defaultValue: "Add date" })}
                  </span>
                </button>
              </div>

              {stayPicker && (
                <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-10 rounded-2xl p-3" style={S.popover}>
                  <StayRangePicker
                    checkIn={date}
                    checkOut={checkOut}
                    minNights={minNights}
                    onChange={({ checkIn: a, checkOut: b }) => {
                      setDate(a);
                      setCheckOut(b);
                      setSelectedTime(null);
                      if (a && !b) setStayPicker("out");
                      if (a && b) setStayPicker(null);
                    }}
                  />
                </div>
              )}

              {date && checkOut && (
                <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#E5B265]/15 px-3 py-1 font-sans text-[12px] font-bold text-[#8a5e15]">
                  <Icon name="ri-moon-line" className="text-[13px]" aria-hidden />
                  {nights} {nights === 1
                    ? t("activity.night_singular", { defaultValue: "night" })
                    : t("activity.nights_plural", { defaultValue: "nights" })}
                </span>
              )}
              {minNights > 1 && !checkOut && (
                <p className="mt-2 font-sans text-[11px] text-[#5a5d77]">
                  <Icon name="ri-information-line" className="me-1" aria-hidden />
                  {t("activity.min_nights", { defaultValue: "Minimum stay" })}: {minNights} {t("activity.nights_plural", { defaultValue: "nights" })}
                </p>
              )}
            </div>
          )}

          {typePrice !== 2 && (
            <>
              <div className="flex items-center justify-between">
                <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#5a5d77]">
                  {t("activity.pick_a_date", { defaultValue: "Pick a date" })}
                </p>
                <button
                  type="button"
                  onClick={() => setExpandedCalendar((v) => !v)}
                  aria-expanded={expandedCalendar}
                  className="flex min-h-[34px] cursor-pointer select-none items-center gap-1.5 rounded-full px-3.5 py-1 font-sans text-[11px] font-bold text-[#110E39] transition active:scale-[0.97] hover:-translate-y-[1px]"
                  style={S.pill}
                >
                  <Icon name={expandedCalendar ? "ri-list-check" : "ri-calendar-line"} className={`text-[12px]`} aria-hidden="true" />
                  {expandedCalendar
                    ? t("activity.quick_view", { defaultValue: "Quick view" })
                    : t("activity.full_calendar", { defaultValue: "Full calendar" })}
                </button>
              </div>

              {!expandedCalendar ? (
                <div className="mj-step-in mt-2 grid grid-cols-7 gap-1.5">
                  {days.map((d) => {
                    const active = date === d.iso;
                    return (
                      <button
                        key={d.iso}
                        type="button"
                        onClick={() => selectDate(d.iso)}
                        className={`relative flex flex-col items-center justify-center rounded-xl px-1 py-2 font-sans transition ${
                          active ? "text-white" : "text-[#110E39]"
                        }`}
                        style={{
                          background: active ? S.cta : S.insetColor,
                          boxShadow: active ? BEVEL_CHIP_ON : undefined,
                        }}
                      >
                        <span
                          className={`text-[9.5px] font-bold uppercase tracking-[0.12em] ${active ? "text-white/70" : "text-[#5a5d77]"}`}
                        >
                          {d.day}
                        </span>
                        <span className="font-heading text-[18px] font-bold leading-none tabular-nums">
                          {d.num}
                        </span>
                        <span
                          className={`mt-0.5 text-[8.5px] uppercase tracking-[0.14em] ${active ? "text-white/65" : "text-[#5a5d77]"}`}
                        >
                          {d.month}
                        </span>
                        {active && (
                          <span
                            aria-hidden
                            className="absolute -bottom-[3px] left-1/2 h-[6px] w-[6px] -translate-x-1/2 rounded-full bg-[#E5B265] shadow-[0_0_0_2px_#110E39]"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div
                  className="mj-step-in mt-2 rounded-2xl p-3"
                  style={S.inset}
                >
                  <CalendarGrid selected={date} onSelect={selectDate} isDateDisabled={calendarDisabled} maxDate={calendarMaxDate} allowedDates={fixedTripDates ? tripDates : undefined} />
                </div>
              )}

              {date && (
                <p className="mt-2 font-sans text-[11.5px] font-semibold text-[#16a34a]">
                  <Icon name="ri-check-line" className="me-1" aria-hidden="true" />
                  {t("activity.selected", { defaultValue: "Selected" })}: {formatDate(date, i18n.language)}
                  {selectedTime ? ` · ${slotLabel(selectedTime)}` : ""}
                </p>
              )}
            </>
          )}

          {date && typePrice !== 2 && !(fixedTripDates && rawPackageTimeSlots.length === 0) && (
            <div className="mt-3">
              <div className="flex items-center justify-between gap-3">
                <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#5a5d77]">
                  {t("activity.available_times", { defaultValue: "Available times" })}
                </p>
                {selectedTime && (
                  <button
                    type="button"
                    onClick={() => setTimesOpen((v) => !v)}
                    aria-expanded={timesOpen}
                    className="flex min-h-[34px] cursor-pointer select-none items-center gap-1.5 rounded-full px-3.5 py-1 font-sans text-[11px] font-bold text-[#110E39] transition active:scale-[0.97] hover:-translate-y-[1px]"
                    style={S.pill}
                  >
                    <Icon name="ri-time-line" className="text-[12px]" aria-hidden="true" />
                    {timesOpen
                      ? t("activity.hide_times", { defaultValue: "Hide" })
                      : <span dir="ltr">{slotLabel(selectedTime)}</span>}
                  </button>
                )}
              </div>
              {(timesOpen || !selectedTime) && (
                <div className="mj-step-in mt-2">
                  <TimeSlotsSection
                    rawSlots={rawPackageTimeSlots}
                    visibleSlots={packageTimeSlots}
                    slug={slug}
                    date={date}
                    selectedTime={selectedTime}
                    onSelectTime={selectTime}
                    labelFor={slotLabel}
                    sameDayCutoff={sameDayCutoff}
                    title={title}
                  />
                </div>
              )}
            </div>
          )}

          {typePrice === 3 && (
            <div
              className="mt-4 flex items-center justify-between rounded-2xl px-4 py-3"
              style={S.inset}
            >
              <div>
                <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#5a5d77]">
                  {unitLabels.title}
                </p>
                <p className="mt-0.5 font-sans text-[14px] font-bold text-[#110E39]">
                  {groups} {unitLabels.count(groups)}
                </p>
              </div>
              <Stepper value={groups} onChange={setGroups} min={1} max={20} />
            </div>
          )}

          {stayUnitsOn && (
            <div
              className="mt-4 flex items-center justify-between rounded-2xl px-4 py-3"
              style={S.inset}
              data-testid="sidebar-stay-units"
            >
              <div>
                <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#5a5d77]">
                  {t("booking_sheet.stay_units_title", { defaultValue: "Tents or lodges" })}
                </p>
                <p className="mt-0.5 font-sans text-[12px] font-medium text-[#5a5d77]">
                  {t("booking_sheet.stay_units_sub", { defaultValue: "Each one priced per night" })}
                </p>
              </div>
              <Stepper value={groups} onChange={setGroups} min={1} max={10} />
            </div>
          )}

          <div ref={guestWrapRef} className="relative mt-4">
            <button
              type="button"
              onClick={() => {
                setDraftGuests(guests);
                setGuestsOpen((v) => !v);
              }}
              aria-expanded={guestsOpen}
              className="flex w-full items-center justify-between gap-3 rounded-2xl px-3.5 py-2.5 transition"
              style={S.inset}
            >
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[rgba(29,22,89,0.06)]">
                  <Icon name="ri-group-line" className="text-[14px] text-[#110E39]" aria-hidden="true" />
                </span>
                <div className="flex flex-col text-start leading-tight">
                  <span className="font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#5a5d77]">
                    {t("activity.guests", { defaultValue: "Guests" })}
                  </span>
                  <span className="font-sans text-[13.5px] font-bold text-[#110E39]">
                    {guestSummary}
                  </span>
                </div>
              </div>
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[linear-gradient(135deg,#2E2878_0%,#1A1454_48%,#110E39_100%)] text-white">
                <Icon name="ri-edit-2-line" className="text-[13px]" aria-hidden="true" />
              </span>
            </button>

            {guestsOpen && (
              <div className="mt-2 overflow-hidden rounded-2xl border border-white/70 bg-white/95 shadow-[0_24px_60px_rgba(4,0,51,0.18)] backdrop-blur-2xl">
                <div className="flex items-center justify-between border-b border-[rgba(4,0,51,0.06)] bg-gradient-to-r from-[rgba(244,241,255,0.7)] to-white/60 px-4 py-3">
                  <div>
                    <p className="font-heading text-[15px] font-bold text-[#110E39]">
                      {t("activity.whos_coming", { defaultValue: "Who's coming?" })}
                    </p>
                    {maxGuests && (
                      <p className="mt-0.5 font-sans text-[11px] text-[#5a5d77]">
                        {t("activity.max_guests", { defaultValue: "Max" })} {maxGuests} {lowerNoun(t("activity.guests", { defaultValue: "guests" }), i18n.language)}
                      </p>
                    )}
                  </div>
                  <span className="rounded-full bg-[rgba(29,22,89,0.06)] px-2 py-0.5 font-sans text-[11px] font-bold text-[#110E39]">
                    {draftTotal}{" "}
                    {draftTotal === 1
                      ? t("activity.guest", { defaultValue: "guest" })
                      : lowerNoun(t("activity.guests", { defaultValue: "guests" }), i18n.language)}
                  </span>
                </div>
                <div className="px-4 divide-y divide-[rgba(4,0,51,0.05)]">
                  {availableGuestTypes.map((type) => {
                    const meta = GUEST_META[type];
                    const rowMin =
                      type === "infants" ? meta.min : guestCounterMins(minParty, draftGuests)[type];
                    return (
                      <div key={type} className="flex items-center gap-3 py-2">
                        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[rgba(29,22,89,0.06)]">
                          <Icon name={meta.icon} className={`text-[16px] text-[#110E39]`} aria-hidden="true" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="font-sans text-[13.5px] font-bold leading-tight text-[#110E39]">
                            {t(meta.labelKey, { defaultValue: meta.defaultLabel })}
                          </p>
                          <p className="font-sans text-[11.5px] leading-snug text-[#5a5d77]">
                            {isTableFlow ? (type === "children" ? tableChildrenSub : null) : ageLabels[type]}
                            {typePrice !== 2 && typePrice !== 3 && !isTableFlow && !spaFlow && (
                              <>
                                <span className="mx-1 text-[#c1c3d4]">·</span>
                                <span className="font-bold text-[#110E39]">
                                  {fmtMoney(guestPrices[type])} {mad}
                                </span>
                              </>
                            )}
                          </p>
                        </div>
                        <Stepper
                          value={draftGuests[type]}
                          onChange={(v) => setDraftGuests((g) => ({ ...g, [type]: v }))}
                          min={rowMin}
                          max={
                            maxGuests
                              ? Math.max(
                                  rowMin,
                                  draftGuests[type] +
                                    Math.max(0, maxGuests - (draftGuests.adults + draftGuests.children + draftGuests.infants)),
                                )
                              : 20
                          }
                        />
                      </div>
                    );
                  })}
                </div>
                {minParty > 1 && (
                  <p className="px-4 pb-3 font-sans text-[11.5px] font-medium text-[#5a5d77]">
                    {t("activity.min_people", { count: minParty, defaultValue: "Minimum {{count}} people" })}
                  </p>
                )}
                <div className="flex items-center gap-2 border-t border-[rgba(4,0,51,0.06)] bg-[rgba(244,241,255,0.4)] p-3">
                  <button
                    type="button"
                    onClick={() => setDraftGuests({ adults: minParty, children: 0, infants: 0 })}
                    className="rounded-full px-3 py-1.5 font-sans text-[12px] font-bold text-[#5a5d77] transition hover:text-[#110E39]"
                  >
                    {t("common.reset", { defaultValue: "Reset" })}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setGuests(draftGuests);
                      setGroups((g) => groupsForParty({ typePrice, groups: g, guests: draftGuests, unitCap: packages?.[0]?.maxGuests ?? null }));
                      setGuestsOpen(false);
                    }}
                    className="ms-auto rounded-full bg-[linear-gradient(180deg,#2E2878_0%,#1A1454_50%,#110E39_100%)] px-4 py-2 font-sans text-[12.5px] font-bold text-white shadow-[0_6px_16px_rgba(17,14,57,0.28),inset_0_1.5px_0_rgba(255,255,255,0.22),inset_0_-1.5px_0_rgba(0,0,0,0.25)] transition hover:scale-[1.02]"
                  >
                    {t("common.apply", { defaultValue: "Apply" })}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-5" style={S.footer}>
          {/* A table is held, not sold: no price, no total, no "from X MAD". */}
          {isTableFlow ? (
            <div>
              <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#5a5d77]">
                {t("activity.table_reservation", { defaultValue: "Table reservation" })}
              </p>
              <p className="mt-1 font-heading text-[20px] font-bold leading-none text-[#110E39]">
                {t("activity.reserve_a_table", { defaultValue: "Reserve a table" })}
              </p>
              <p className="mt-1 font-sans text-[11.5px] font-medium text-[#5a5d77]">
                {t("activity.table_promise_short", {
                  defaultValue: "Free to reserve",
                })}
              </p>
            </div>
          ) : (
          <div className="flex flex-wrap items-end justify-between gap-x-2 gap-y-2.5">
            <div>
              <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#5a5d77]">
                {spaFlow
                  ? t("activity.starting_from")
                  : typePrice === 0
                  ? t("activity.person", { defaultValue: "Per person" })
                  : typePrice === 2
                    ? `${t("activity.total", { defaultValue: "Total" })} · ${fmt(nights)} ${nights === 1 ? t("activity.night_singular", { defaultValue: "night" }) : t("activity.nights_plural", { defaultValue: "nights" })}${totalGuests > 0 ? ` · ${guestSummary}` : ""}`
                    : typePrice === 3
                      ? `${t("activity.total", { defaultValue: "Total" })} · ${fmt(groups)} ${unitLabels.count(groups)}${totalGuests > 0 ? ` · ${guestSummary}` : ""}`
                      : totalGuests > 0
                        ? `${t("activity.total", { defaultValue: "Total" })} · ${guestSummary}`
                        : t("activity.starting_from")}
              </p>
              <p className="mt-1 font-heading text-[26px] font-bold leading-none text-[#110E39]">
                {spaFlow || typePrice === 0 ? fmtMoney(price) : money.parts(shownEstimate).value}{" "}
                <span className="text-[14px]">{mad}</span>
              </p>
              <p className="mt-1 font-sans text-[11px] font-medium text-[#5a5d77]">
                {spaFlow
                  ? t("activity.per_treatment", { defaultValue: "per treatment" })
                  : typePrice === 0 && totalGuests > 1
                  ? guestSummary
                  : typePrice === 2
                    ? stayUnitsOn && groups > 1
                      ? `${fmtMoney(price)} ${mad} × ${fmt(Math.max(1, nights))} × ${fmt(groups)}`
                      : nights > 1
                      ? `${fmtMoney(price)} ${mad} × ${fmt(nights)} ${t("activity.nights_plural", { defaultValue: "nights" })}`
                      : `${fmtMoney(price)} ${mad} ${t("activity.per_night", { defaultValue: "per night" })}`
                    : typePrice === 3
                      ? groups > 1
                        ? `${fmtMoney(price)} ${mad} × ${fmt(groups)} ${unitLabels.count(groups)}`
                        : `${fmtMoney(price)} ${mad} ${unitLabels.per}`
                      : t(PRICE_LABEL_KEYS[typePrice])}
              </p>
            </div>
            {/* Soft gold "No fees" pill. Inline style for the gold tones so we
                don't rely on Tailwind's content scanner picking up arbitrary
                hex values that aren't used anywhere else in the bundle. */}
            {/* When the price box is narrow the pill moves under the price;
                if a long locale still needs two lines, they are balanced so no
                word ("day") sits alone on the second. */}
            {/* A trip says its money in the rows below, so no promise pill. */}
            {!womenTrip && (
            <span
              className="inline-flex max-w-full items-center gap-1 rounded-full px-2.5 py-1 font-sans text-[11px] font-bold leading-[14px] [text-wrap:balance]"
              style={{ background: "#fff6e1", color: "#8a5a0f" }}
            >
              <Icon name="ri-coin-line" className="text-[12px] text-[#E5B265]" aria-hidden />
              {/* One wording for the promise everywhere (lab round 26, item
                  193); a camp paid by link or a group deposit keeps "No fees",
                  since "pay on the day" would not be true there. */}
              <span className="min-w-0 [text-wrap:balance]">
                {paysByLink(slug) || groupDepositFor(slug, totalGuests)
                  ? t("activity.no_fees", { defaultValue: "No fees" })
                  : t("activity.promise_short", { defaultValue: "Free to reserve · Pay on the day" })}
              </span>
            </span>
            )}
          </div>
          )}

          {/* Said beside the price, not discovered in the sheet: a solo guest
              read "360 MAD per person" and met "Minimum 2 people, 720 MAD"
              only after tapping (traveller test, 2026-11-01). Every line comes
              from the activity's own data. */}
          {!isTableFlow && !spaFlow && !womenTrip && (minParty > 1 || adultsOnly || unitCap > 0) && (
            <ul className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1 font-sans text-[11.5px] font-semibold text-[#5a5d77]" data-testid="sidebar-party-rules">
              {minParty > 1 && (
                <li className="flex items-center gap-1">
                  <Icon name="ri-group-line" className="text-[12px]" aria-hidden />
                  {t("activity.min_people", { count: minParty, defaultValue: "Minimum {{count}} people" })}
                </li>
              )}
              {unitCap > 0 && (
                <li className="flex items-center gap-1">
                  <Icon name="ri-user-line" className="text-[12px]" aria-hidden />
                  {t("activity.up_to_people_per_unit", {
                    count: unitCap,
                    per: unitLabels.per,
                    // City sites carry no booking locale keys of their own.
                    defaultValue: "Up to {{count}} people {{per}}",
                  })}
                </li>
              )}
              {adultsOnly && (
                <li className="flex items-center gap-1">
                  <Icon name="ri-user-star-line" className="text-[12px]" aria-hidden />
                  {t("activity.adults_only_from", { min: adultMinAge, defaultValue: "Adults only, {{min}}+" })}
                </li>
              )}
            </ul>
          )}

          {/* Per-person pricing buries the number the guest actually pays: at
              4 adults + 3 children the 1,800 MAD total was 11px grey beside a
              26px 450 MAD. Own row, own weight. */}
          {womenTrip && !tripFull && tripDepartures.length > 0 && (
            <TripDeparturePicker
              className="mt-3"
              compact
              departures={tripDepartures}
              value={tripDeparture}
              onChange={setTripDeparture}
            />
          )}
          {womenTrip && <WomenTripFooter
            trip={womenTrip}
            date={date}
            tripFull={tripFull}
            seatsShown={tripSeatsLeftOn(womenTrip, date) ?? fewestSeatsLeft(womenTrip, todayIso)}
            shownTotal={
              typePrice === 0
                ? money.unit(guestPrices.adults) * Math.max(1, guests.adults) +
                  Math.max(0, money.unit(guestPrices.children)) * guests.children
                : shownEstimate
            }
            seats={Math.max(1, guests.adults + guests.children)}
            unit={money.unit}
            format={money.parts}
          />}
          {!womenTrip && !isTableFlow && !spaFlow && typePrice === 0 && totalGuests > 1 && (
            <div
              className="mt-3 flex items-center justify-between gap-3 rounded-2xl px-4 py-2.5"
              style={S.inset}
            >
              <span className="font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#5a5d77]">
                {t("activity.total", { defaultValue: "Total" })}
              </span>
              <span className="font-heading text-[19px] font-bold leading-none text-[#110E39]">
                {money.parts(shownEstimate).value} <span className="text-[12px]">{mad}</span>
              </span>
            </div>
          )}
          {(() => {
            const stayMissingDates = typePrice === 2 && (!date || !checkOut);
            const baseCls =
              "mt-4 block rounded-full py-3.5 text-center font-sans text-[14px] font-bold shadow-[0_12px_26px_rgba(17,14,57,0.28)] transition";
            if (stayMissingDates) {
              return (
                <>
                  <button
                    type="button"
                    disabled
                    aria-disabled="true"
                    className={`${baseCls} w-full cursor-not-allowed bg-[rgba(17,14,57,0.40)] text-white`}
                  >
                    {t("activity.book_now")}
                  </button>
                  <p className="mt-2 text-center font-sans text-[11.5px] text-[#5a5d77]">
                    <Icon name="ri-information-line" className="me-1" aria-hidden />
                    {t("activity.select_check_in_out_required", {
                      defaultValue: "Select check-in and check-out dates to continue.",
                    })}
                  </p>
                </>
              );
            }
            const ctaCls = `${baseCls} text-white hover:scale-[1.01]`;
            const ctaStyle = { background: S.cta, boxShadow: `0 12px 26px rgba(17,14,57,0.28), ${BEVEL_INSET_NAVY}` };
            // A trip reserves its seat in the sheet at every width. When every
            // date is full the button becomes the waiting list, on WhatsApp.
            if (womenTrip) {
              if (tripFull || tripSeatsLeftOn(womenTrip, date) === 0) {
                return (
                  <a
                    href={waitingListHref(
                      date
                        ? t("women.waiting_list_message_date", { title, date: formatDate(date, i18n.language) })
                        : t("women.waiting_list_message", { title }),
                    )}
                    target="_blank"
                    rel="noreferrer"
                    className={`${baseCls} w-full text-[#110E39]`}
                    style={{ background: "#fff", boxShadow: `0 0 0 1.5px ${PW.controlEdge}, 0 8px 20px -12px rgba(17,14,57,0.3)` }}
                    data-testid="trip-waiting-list"
                  >
                    {t("women.join_waiting_list")}
                  </a>
                );
              }
              return (
                <button
                  type="button"
                  onClick={() => onBookSheet?.(bookSelection)}
                  className={`${ctaCls} w-full`}
                  style={ctaStyle}
                  data-testid="trip-reserve"
                >
                  {t("women.reserve_seat")}
                </button>
              );
            }
            const ctaLabel = isTableFlow
              ? t("activity.reserve_a_table", { defaultValue: "Reserve a table" })
              : t("activity.book_now");
            // A restaurant has no /packages page to send desktop to, and a club
            // books a night rather than a package. Both reserve in the sheet, at
            // every width.
            if (onBookSheet && (isTableFlow || sheetOnly)) {
              return (
                <button
                  type="button"
                  onClick={() => onBookSheet(bookSelection)}
                  className={`${ctaCls} w-full`}
                  style={ctaStyle}
                >
                  {ctaLabel}
                </button>
              );
            }
            if (onBookSheet) {
              return (
                <>
                  <button
                    type="button"
                    onClick={() => onBookSheet(bookSelection)}
                    className={`${ctaCls} w-full lg:hidden`}
                    style={ctaStyle}
                  >
                    {ctaLabel}
                  </button>
                  <a
                    href={`${bookHrefBase}${bookSearch}`}
                    className={`${ctaCls} hidden lg:block`}
                    style={ctaStyle}
                  >
                    {ctaLabel}
                  </a>
                </>
              );
            }
            return (
              <a
                href={`${bookHrefBase}${bookSearch}`}
                className={ctaCls}
                style={ctaStyle}
              >
                {t("activity.book_now")}
              </a>
            );
          })()}
          <p className="mt-2 flex items-center justify-center gap-1.5 font-sans text-[11.5px] font-semibold text-[#5a5d77]">
            <Icon name="ri-shield-check-line" className="text-[#10b981]" aria-hidden="true" />
            {womenTrip
              ? t("women.pay_line")
              : isTableFlow
              ? t("activity.table_pay_restaurant", {
                  defaultValue: "We hold the table. You pay the restaurant for what you order.",
                })
              : paysByLink(slug)
                ? t("activity.pay_by_link_long", {
                    defaultValue: "Reserve now, we email you a secure payment link for the full amount",
                  })
                : groupDepositFor(slug, totalGuests)
                  ? t("activity.group_deposit_title", {
                      percent: groupDepositFor(slug, totalGuests)?.percent,
                    })
                  : t("activity.pay_on_arrival_long")}
          </p>
          <div className="mt-3 flex items-center justify-between gap-2">
            {onToggleWishlist !== undefined && (
              <SaveToastButton
                saved={wishlisted}
                onToggle={onToggleWishlist}
                aria-label={t("activity.save", { defaultValue: "Save" })}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-full border py-2 font-sans text-[12px] font-bold transition ${
                  wishlisted
                    ? "border-pink-200 bg-pink-50 text-pink-600"
                    : "border-[rgba(4,0,51,0.10)] bg-white/70 text-[#110E39] hover:bg-white"
                }`}
              >
                <Icon name={wishlisted ? "ri-heart-fill" : "ri-heart-line"} />
                {t("activity.save", { defaultValue: "Save" })}
              </SaveToastButton>
            )}
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              aria-label={t("activity.share", { defaultValue: "Share" })}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-[rgba(4,0,51,0.10)] bg-white/70 py-2 font-sans text-[12px] font-bold text-[#110E39] transition hover:bg-white"
            >
              <Icon name="ri-share-line" />
              {t("activity.share", { defaultValue: "Share" })}
            </button>
          </div>
        </div>
      </div>

      <NeedHelpCard subject={title} />

      {shareOpen && renderShareModal?.({
        url,
        title,
        thumbnail,
        cityName,
        price,
        onClose: () => setShareOpen(false),
      })}
    </aside>
  );
}

/**
 * The trip's half of the sidebar footer: the seats on the chosen date (or the
 * fewest on any date), the three money rows, and the pink note on who gets
 * the deposit, or that the trip is full.
 */
function WomenTripFooter({
  trip,
  date,
  tripFull,
  seatsShown,
  shownTotal,
  seats,
  unit,
  format,
}: {
  trip: WomenTripInfo;
  date: string | null;
  tripFull: boolean;
  seatsShown: number | null;
  shownTotal: number;
  seats: number;
  unit: (mad: number) => number;
  format: (amount: number) => { value: string; currency: string };
}) {
  const { t } = useTranslation();
  const depositNote = useDepositNote(trip.organiser?.name);
  const rows = tripMoneyRows(shownTotal, trip.depositAmount, seats, unit);
  const dateFull = tripFull || tripSeatsLeftOn(trip, date) === 0;
  return (
    <div className="mt-3 flex flex-col gap-2.5" data-testid="trip-footer">
      <TripSeatsBar seatLimit={trip.seatLimit} seatsLeft={tripFull ? 0 : seatsShown} />
      {rows && (
        <TripMoneyRowsView
          rows={rows}
          seats={seats}
          format={format}
          className="border-t border-[rgba(17,14,57,0.08)] pt-1.5"
        />
      )}
      <TripNote testId="trip-note">
        {tripFull ? t("women.full_note") : dateFull ? t("women.date_full_note") : depositNote}
      </TripNote>
    </div>
  );
}
